/**
 * Global scroll-anchor handling.
 *
 * Sections expose a merchant-defined "Scroll ID" (rendered by the
 * scroll-anchor snippet as <div id="..." class="scroll-anchor">). Any link
 * on the page whose hash matches an element id — a scroll anchor or any
 * other id — is smooth-scrolled to, offset below the fixed header, and the
 * URL hash is updated so the position is deep-linkable.
 *
 * Native jumps (initial page load with a hash, open-in-new-tab) are handled
 * by CSS scroll-margin-top on .scroll-anchor; a load-time correction below
 * covers hash targets that lack that rule.
 */
(function () {
  function getHeaderHeight() {
    var cssVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    var fromVar = parseFloat(cssVar);
    if (fromVar > 0) return fromVar;
    var legacy = document.querySelector('.header-group-wrapper');
    if (legacy && legacy.clientHeight > 0) return legacy.clientHeight;
    var v2 = document.querySelector('.v2mn-header');
    if (v2 && v2.clientHeight > 0) return v2.clientHeight;
    return 80;
  }

  function scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  function scrollToTarget(target) {
    var top = target.getBoundingClientRect().top + window.pageYOffset - getHeaderHeight();
    window.scrollTo({ top: Math.max(top, 0), behavior: scrollBehavior() });
  }

  function hashTarget(hash) {
    if (!hash || hash === '#') return null;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (err) {
      id = hash.slice(1);
    }
    return document.getElementById(id);
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target && event.target.closest ? event.target.closest('a[href*="#"]') : null;
    if (!link) return;
    if (link.closest('anchor-links')) return; // legacy component handles its own clicks
    if (link.target && link.target !== '_self') return;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (err) {
      return;
    }
    // Only intercept same-page links; cross-page links navigate normally and
    // the destination page handles its own hash.
    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;
    if (url.search !== window.location.search) return;

    // Leave the accessibility skip link fully native (it must move focus).
    if (url.hash === '#MainContent') return;

    var target = hashTarget(url.hash);
    if (!target) return;

    event.preventDefault();
    scrollToTarget(target);
    // Preserve native focus behavior for focusable targets (e.g. form-error
    // summary links pointing at inputs).
    if (target.matches('a, button, input, select, textarea, [tabindex]')) {
      target.focus({ preventScroll: true });
    }
    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', url.hash);
    }
  });

  // Deep links: an inline head script (theme.liquid) strips the hash before
  // the parser can perform the native instant jump, stashing it on
  // window.__pendingScrollHash. On load, restore the hash and smooth-scroll
  // from the top instead. Lazy-loaded images above the target can shift its
  // position mid-animation, so re-correct once the scroll settles.
  window.addEventListener('load', function () {
    var hash = window.__pendingScrollHash;
    if (!hash) {
      // Whitelisted hashes (see theme.liquid inline script) drive app logic —
      // leave them entirely to their owners (Rivo, studio router, mini-quiz).
      if (/^#(rivo|\/|MiniQuizNewsletter)/i.test(window.location.hash)) return;
      // fallback (hash survived, e.g. inline script missing): offset-correct
      var survivor = hashTarget(window.location.hash);
      if (survivor && !survivor.classList.contains('scroll-anchor')) {
        window.scrollTo({
          top: survivor.getBoundingClientRect().top + window.pageYOffset - getHeaderHeight(),
          behavior: 'auto'
        });
      }
      return;
    }
    window.__pendingScrollHash = null;
    window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);
    var target = hashTarget(hash);
    if (!target) return;
    requestAnimationFrame(function () {
      scrollToTarget(target);
    });
    // Re-correct once the scroll settles (lazy images above the target can
    // shift its position mid-animation) — but never fight the user: any
    // manual interaction cancels the correction.
    var cancelled = false;
    var cancel = function () { cancelled = true; };
    ['wheel', 'touchstart', 'mousedown', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, cancel, { passive: true, once: true });
    });
    var settle = function () {
      if (cancelled) return;
      cancelled = true; // run at most once
      var top = target.getBoundingClientRect().top + window.pageYOffset - getHeaderHeight();
      if (Math.abs(window.pageYOffset - top) > 4) {
        window.scrollTo({ top: Math.max(top, 0), behavior: 'auto' });
      }
    };
    if ('onscrollend' in window) {
      var once = function () {
        window.removeEventListener('scrollend', once);
        setTimeout(settle, 50);
      };
      window.addEventListener('scrollend', once);
      setTimeout(settle, 2500); // backstop if scrollend never fires
    } else {
      setTimeout(settle, 1200);
    }
  });
})();
